const SAVINGS_LISTENER_EVENT = 'trade-show:savings-updated';

export function tradeShowCalculatorComponent(config = {}) {
    const {
        seedOilPrice = 1.0393,
        tallowPrice = 1.1155,
        tallowRatio = 2 / 3,
        rotationInterval = 5,
        idleTimeout = 60,
        attractImageCount = 0,
    } = config;

    return {
        state: 'attract',
        locations: '',
        oilPerLocation: '',
        estimatedSavings: 0,
        attractIndex: 0,
        cardHeight: 0,
        // Touch keypad — `activeKeypadField` is the form field name the keypad
        // currently writes to. Null = no field focused, keypad hidden.
        activeKeypadField: null,
        keypadMaxDigits: 4,
        _imageRotationTimer: null,
        _idleTimer: null,
        _resizeHandler: null,
        _resizeObserver: null,

        init() {
            this.startAttractRotation();
            this.resetIdleTimer();

            // Card height tracks the currently active state's natural content height.
            // CSS transitions the change so swapping states resizes the card smoothly.
            this.$nextTick(() => {
                this.updateCardHeight();
                this.$watch('state', () => this.$nextTick(() => this.updateCardHeight()));

                // Recompute when any state's content actually changes size
                if (typeof ResizeObserver !== 'undefined') {
                    this._resizeObserver = new ResizeObserver(() => this.updateCardHeight());
                    ['cardInput', 'cardResults'].forEach((name) => {
                        const el = this.$refs[name];
                        if (el) this._resizeObserver.observe(el);
                    });
                }
            });
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(() => this.updateCardHeight());
            }
            this._resizeHandler = () => this.updateCardHeight();
            window.addEventListener('resize', this._resizeHandler);
        },

        destroy() {
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
            }
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
            }
            this.stopAttractRotation();
            this.clearIdleTimer();
        },

        updateCardHeight() {
            // Pick the ref for the currently active state (fall back to input when on attract)
            const refsByState = {
                input: 'cardInput',
                results: 'cardResults',
            };
            const refName = refsByState[this.state] || 'cardInput';
            const el = this.$refs[refName];
            if (el) this.cardHeight = el.scrollHeight;
        },

        startAttractRotation() {
            this.stopAttractRotation();
            if (attractImageCount <= 1) return;
            this._imageRotationTimer = setInterval(() => {
                this.attractIndex = (this.attractIndex + 1) % attractImageCount;
            }, Math.max(1, rotationInterval) * 1000);
        },

        stopAttractRotation() {
            if (this._imageRotationTimer) {
                clearInterval(this._imageRotationTimer);
                this._imageRotationTimer = null;
            }
        },

        // ----- State transitions -----
        goToAttract() {
            this.state = 'attract';
            this.startAttractRotation();
            this.clearIdleTimer();
        },

        goToInput() {
            this.state = 'input';
            this.activeKeypadField = null;
            this.stopAttractRotation();
            this.resetIdleTimer();
        },

        // ----- Keypad -----
        focusKeypadField(name) {
            this.activeKeypadField = name;
            this.resetIdleTimer();
        },

        _setActiveValue(value) {
            const field = this.activeKeypadField;
            if (field === 'locations') this.locations = value;
            else if (field === 'oilPerLocation') this.oilPerLocation = value;
        },

        _getActiveValue() {
            const field = this.activeKeypadField;
            if (field === 'locations') return String(this.locations || '');
            if (field === 'oilPerLocation') return String(this.oilPerLocation || '');
            return '';
        },

        keypadPress(digit) {
            if (!this.activeKeypadField) return;
            const current = this._getActiveValue();
            if (current.length >= this.keypadMaxDigits) return;
            const next = (current + digit).replace(/^0+(?=\d)/, '');
            this._setActiveValue(next);
            this.resetIdleTimer();
        },

        keypadBackspace() {
            if (!this.activeKeypadField) return;
            const current = this._getActiveValue();
            this._setActiveValue(current.slice(0, -1));
            this.resetIdleTimer();
        },

        keypadClear() {
            if (!this.activeKeypadField) return;
            this._setActiveValue('');
            this.resetIdleTimer();
        },

        handleKeypadKeydown(event) {
            if (!this.activeKeypadField) return;
            const key = event.key;
            if (/^[0-9]$/.test(key)) {
                event.preventDefault();
                this.keypadPress(key);
            } else if (key === 'Backspace') {
                event.preventDefault();
                this.keypadBackspace();
            } else if (key === 'Enter' || key === 'Escape') {
                event.preventDefault();
                this.activeKeypadField = null;
            }
        },

        // ----- Calculator math -----
        calculate() {
            const locs = parseFloat(this.locations) || 0;
            const oilPerWeek = parseFloat(this.oilPerLocation) || 0;
            const oilPerMonth = oilPerWeek * 4;

            const seedCostMonth = locs * oilPerMonth * seedOilPrice;
            const seedCostYear = seedCostMonth * 12;

            const tallowMonthlyVolume = oilPerMonth * tallowRatio;
            const tallowCostMonth = locs * tallowMonthlyVolume * tallowPrice;
            const tallowCostYear = tallowCostMonth * 12;

            this.estimatedSavings = Math.max(0, Math.round(seedCostYear - tallowCostYear));
            this.state = 'results';
            this.resetIdleTimer();

            this.$dispatch(SAVINGS_LISTENER_EVENT, {
                savings: this.estimatedSavings,
                locations: locs,
                oilPerLocation: oilPerWeek,
            });
        },

        get formattedSavings() {
            return '$' + (this.estimatedSavings || 0).toLocaleString('en-US');
        },

        get canCalculate() {
            return parseFloat(this.locations) > 0 && parseFloat(this.oilPerLocation) > 0;
        },

        startOver() {
            this.activeKeypadField = null;
            this.attractIndex = 0;
            this.goToAttract();
            // Defer the reset so the leaving results state doesn't visibly
            // flip its savings number to $0 mid-fade.
            setTimeout(() => {
                this.locations = '';
                this.oilPerLocation = '';
                this.estimatedSavings = 0;
            }, 250);
        },

        // ----- Idle timer -----
        onActivity() {
            if (this.state === 'attract') return;
            this.resetIdleTimer();
        },

        resetIdleTimer() {
            this.clearIdleTimer();
            const seconds = Math.max(5, idleTimeout);
            this._idleTimer = setTimeout(() => {
                this.handleIdleExpired();
            }, seconds * 1000);
        },

        clearIdleTimer() {
            if (this._idleTimer) {
                clearTimeout(this._idleTimer);
                this._idleTimer = null;
            }
        },

        handleIdleExpired() {
            this.attractIndex = 0;
            this.goToAttract();
            // Defer the reset until after the state crossfade so the user
            // doesn't see savings/inputs visibly clear mid-transition.
            setTimeout(() => {
                this.locations = '';
                this.oilPerLocation = '';
                this.estimatedSavings = 0;
            }, 250);
        },
    };
}

export default tradeShowCalculatorComponent;
